import Typography from '@/components/Typography';
import { TextStyles } from '@/constants';
import styled from 'styled-components';
import { InsightsSection } from '../TrackingInsights.styles';

/** Figma 20561:27767 — cards 320px wide, 20px apart, content padded 24px. */
export const REVIEW_CARD_WIDTH = 320;

/**
 * The card row runs off the right edge of the viewport (see `ReviewTrack`), so the band clips it
 * rather than letting it widen the page.
 */
export const ReviewsSection = styled(InsightsSection)({
  overflow: 'hidden',
});

/**
 * Horizontal scroller for the cards.
 *
 * The design aligns the first card with the content column and lets the row bleed off the right
 * edge of the viewport. The track is therefore pulled out to both viewport edges by the gap between
 * the column and the viewport, then padded back on both sides by the same amount — so the first card
 * sits on the column, the last one ends on it once the row is scrolled through, and cards in
 * between still travel all the way to the viewport edges instead of being cut off at the column.
 *
 * Below `md` the container is full width, so that gap is exactly its 16px gutter, and the row snaps
 * card by card with a `scroll-padding` of the same 16px. From `md` up the gap depends on the
 * viewport (`50vw - 50%`, margin and padding cancelling even where `100vw` counts a scrollbar); a
 * percentage in `scroll-padding` would resolve against the scrollport rather than the container, so
 * there is no snapping and the arrow buttons align the cards themselves (see `CustomerReviews`).
 *
 * The vertical padding keeps the card shadow from being clipped by the scroll container; the
 * negative top margin and the nav margin give that 8px back so the spacing stays on spec.
 */
export const ReviewTrack = styled.div(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  gap: theme.spacing[20],
  overflowX: 'auto',
  marginTop: `-${theme.spacing[8]}`,
  marginLeft: `-${theme.spacing[16]}`,
  marginRight: `-${theme.spacing[16]}`,
  paddingTop: theme.spacing[8],
  paddingBottom: theme.spacing[8],
  paddingLeft: theme.spacing[16],
  // The same inset after the last card, so at the end of the row it stops on the column's right
  // edge instead of running flush into the viewport edge.
  paddingRight: theme.spacing[16],
  scrollSnapType: 'x mandatory',
  scrollPaddingLeft: theme.spacing[16],
  scrollbarWidth: 'none',

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  '@media (prefers-reduced-motion: no-preference)': {
    scrollBehavior: 'smooth',
  },

  [`${theme.mediaBreakpointUp.md}`]: {
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
    paddingLeft: 'calc(50vw - 50%)',
    paddingRight: 'calc(50vw - 50%)',
    scrollSnapType: 'none',
  },
}));

/**
 * Equal-height cards (the flex row stretches them). The byline is pinned to the bottom so it sits
 * at the same height on every card; the quote is centred in the space above it.
 *
 * A card always takes the opposite surface to its band: white cards on the tinted band (country and
 * region pages), tinted cards on the white band (carrier pages).
 */
export const ReviewCard = styled.figure<{ $onTint?: boolean }>(({ theme, $onTint }) => ({
  flex: `0 0 ${REVIEW_CARD_WIDTH}px`,
  // 16px gutter on each side: keeps a card inside a phone narrower than the 320px card.
  maxWidth: 'calc(100vw - 32px)',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[16],
  margin: 0,
  padding: theme.spacing[24],
  borderRadius: theme.spacing[4],
  backgroundColor: $onTint ? theme.colors.highEmphasisWhite : theme.colors.surface01,
  boxShadow: theme.elevation[3],
  scrollSnapAlign: 'start',
}));

/**
 * The quotation marks come from CSS rather than the copy so the stored review text stays clean
 * and the marks follow the `quotes` property for the active language instead of being hardcoded
 * to the English pair.
 */
export const ReviewQuote = styled(Typography).attrs({
  variant: TextStyles['Body Text Large'],
  type: 'blockquote',
})(({ theme }) => ({
  margin: 'auto 0',
  color: theme.colors.highEmphasisBlack,

  '&::before': { content: 'open-quote' },
  '&::after': { content: 'close-quote' },
}));

export const ReviewAuthor = styled(Typography).attrs({
  variant: TextStyles['Heading 3'],
  type: 'figcaption',
})(({ theme }) => ({
  margin: 0,
  color: theme.colors.highEmphasisBlack,
}));

export const ReviewNav = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing[8],
  marginTop: theme.spacing[16],
}));

export const ReviewNavButton = styled.button(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing[40],
  height: theme.spacing[40],
  padding: 0,
  border: `1px solid ${theme.colors.blue}`,
  borderRadius: '50%',
  backgroundColor: 'transparent',
  color: theme.colors.blue,
  cursor: 'pointer',

  '&:hover:not(:disabled)': {
    backgroundColor: theme.colors.lightBlue,
  },

  '&:disabled': {
    borderColor: theme.colors.disabledBlack,
    color: theme.colors.disabledBlack,
    cursor: 'default',
  },
}));
